import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { catchError, Observable, throwError } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import {
  SKIP_GLOBAL_HTTP_ERROR_TOAST,
  SKIP_HTTP_403_UNAUTHORIZED_NAVIGATE,
} from './http-context-tokens';

/**
 * Extract error message from various response formats.
 * Handles: { message: "..." }, plain string, or default fallback.
 */
function extractErrorMessage(error: HttpErrorResponse, defaultMessage: string): string {
  if (!error.error) {
    return defaultMessage;
  }
  // Object with message property
  if (typeof error.error === 'object' && error.error.message) {
    return error.error.message;
  }
  // Plain string response
  if (typeof error.error === 'string') {
    return error.error;
  }
  return defaultMessage;
}

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next
): Observable<HttpEvent<unknown>> => {
  const injector = inject(Injector);
  const router = inject(Router);
  const toastr = inject(ToastrService);
  const ngZone = inject(NgZone);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage: string;
      let showToast = true;

      const skipUnauthorizedNavigate = req.context.get(SKIP_HTTP_403_UNAUTHORIZED_NAVIGATE);

      switch (error.status) {
        case 0:
          errorMessage = 'No se pudo conectar con el servidor';
          break;
        case 400:
          errorMessage = extractErrorMessage(error, 'Solicitud inválida');
          break;
        case 401:
          errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente.';
          injector.get(AuthService).logout();
          break;
        case 403:
          errorMessage = 'No tienes permisos para realizar esta acción';
          if (!skipUnauthorizedNavigate) {
            router.navigate(['/unauthorized']);
          }
          break;
        case 404:
          // Don't show toast for 404s - let components handle if needed
          errorMessage = 'Recurso no encontrado';
          showToast = false;
          break;
        case 409:
          errorMessage = extractErrorMessage(error, 'El recurso ya existe');
          break;
        case 422:
          errorMessage = extractErrorMessage(error, 'Datos inválidos');
          break;
        case 500:
        case 502:
        case 503:
          errorMessage = 'Error del servidor. Por favor, intenta más tarde.';
          break;
        default:
          errorMessage = extractErrorMessage(error, 'Ha ocurrido un error inesperado');
      }

      const skipGlobalToast = req.context.get(SKIP_GLOBAL_HTTP_ERROR_TOAST);

      if (showToast && !skipGlobalToast) {
        // Run inside NgZone to ensure change detection triggers
        ngZone.run(() => {
          toastr.error(errorMessage, 'Error');
        });
      }

      return throwError(() => error);
    })
  );
};

